import React, { useEffect, useState } from "react";
import { Table, Button, Input, Modal, Form, Space, Typography } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  createSpeciality,
  deleteSpeciality,
  fetchSpecialities,
  updateSpeciality,
} from "../../Redux/Slice/Admin/Speciality";
import { toast } from "react-toastify";

const Specialties = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteKey, setDeleteKey] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const { data: specialityData, loading } = useSelector(
    (state) => state.speciality.fetch
  );

  const {
    data: createData,
    loading: createLoading,
    error: createError,
  } = useSelector((state) => state.speciality.create);
  const {
    data: updateData,
    loading: updateLoading,
    error: updateError,
  } = useSelector((state) => state.speciality.update);
  const {
    data: deleteData,
    loading: deleteLoading,
    error: deleteError,
  } = useSelector((state) => state.speciality.delete);

  console.log("createData",createData);
  console.log("createError",createError);
  

  useEffect(() => {
    dispatch(fetchSpecialities({ specialityName: "" }));
  }, [dispatch]);

  useEffect(() => {
    if (createData) {
      if (createData.status === true) {
        toast.success(createData.message || "Created successfully!");
        dispatch(fetchSpecialities());
        setModalVisible(false);
        form.resetFields();
      } 
    }
    if (createError) {
      toast.error(createError.message || "Creation failed!");
    }
  }, [createError, createData, dispatch, form]);

  useEffect(() => {
    if (updateData) {
      if (updateData.status === true) {
        toast.success(updateData.message || "Updated successfully!");
        dispatch(fetchSpecialities());
        setModalVisible(false);
        form.resetFields();
      }
    }
    if (updateError) {
      toast.error(updateError || "Update failed!");
    }
  }, [updateError, updateData, dispatch, form]);

  useEffect(() => {
    if (deleteData) {
      if (deleteData.status === true) {
        toast.success(deleteData.message || "Deleted successfully!");
        dispatch(fetchSpecialities());
        setDeleteConfirm(false);
      }
    }
    if (deleteError) {
      toast.error(deleteError || "Delete failed!");
    }
  }, [deleteError, deleteData, dispatch]);

  useEffect(() => {
    if (specialityData) {
      const mappedData = specialityData?.data?.map((item) => ({
        ...item,
        key: item.id,
      }));
      setData(mappedData);
    }
  }, [specialityData]);

  const handleSearch = (value) => {
    setSearchValue(value);
    dispatch(fetchSpecialities({ specialityName: value }));
  };

  // Open Create Modal
  const handleOpenCreate = () => {
    setEditingRow(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (record) => {
    setEditingRow(record);
    form.setFieldsValue({
      specialityCode: record.specialityCode,
      specialityName: record.specialityName,
      specialityType: record.specialityType,
    });
    setModalVisible(true);
  };

  // Save (Create or Update)
  const handleSave = () => {
    form.validateFields().then((values) => {
      if (editingRow) {
        // UPDATE payload
        const payload = {
          specialityId: editingRow.specialityId,
          ...values,
        };
        dispatch(updateSpeciality(payload));
      } else {
        // CREATE payload
        const payload = {
          ...values,
        };
        dispatch(createSpeciality(payload));
      }
    });
  };

  // Confirm Delete (you may need deleteSpeciality API here)
  const handleDelete = () => {
    dispatch(deleteSpeciality(deleteKey));
  };

  const columns = [
    {
      title: "Specialty Code",
      dataIndex: "specialityCode",
      key: "specialityCode",
    },
    {
      title: "Specialty Name",
      dataIndex: "specialityName",
      key: "specialityName",
    },
    {
      title: "Specialty Type",
      dataIndex: "specialityType",
      key: "specialityType",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleOpenEdit(record)}
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setDeleteKey(record.specialityId);
              setDeleteConfirm(true);
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header with Search + Create */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Input.Search
          placeholder="Search specialty name"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear // 🔹 adds ❌ clear button
          style={{ width: 300 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleOpenCreate}
        >
          Create
        </Button>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        loading={loading}
      />

      {/* Create / Edit Modal */}
      <Modal
        open={modalVisible}
        title={editingRow ? "Edit Specialty" : "Create New Specialty"}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        okText={editingRow ? "Save" : "Create"}
        destroyOnClose
        confirmLoading={editingRow ? updateLoading : createLoading}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="Specialty Code"
            name="specialityCode"
            rules={[{ required: true, message: "Please enter specialty code" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Specialty Name"
            name="specialityName"
            rules={[{ required: true, message: "Please enter specialty name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Specialty Type"
            name="specialityType"
            rules={[{ required: true, message: "Please enter specialty type" }]}
          >
            <Input />
          </Form.Item>
          <Typography.Text type="secondary">
            The specialty code should be unique and follow your organization’s
            naming convention.
          </Typography.Text>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={deleteConfirm}
        title="Confirm Delete"
        onCancel={() => setDeleteConfirm(false)}
        onOk={handleDelete}
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleteLoading }}
      >
        <Typography>Are you sure you want to delete this specialty?</Typography>
      </Modal>
    </div>
  );
};

export default Specialties;
